import { useEffect, type JSX } from 'react'
import { Bell, X, MessageSquare } from 'lucide-react'
import { useNavigate } from 'react-router'
import { useNotificationsStore } from '../../store/notificationsStore.js'

export default function NotificationToast(): JSX.Element | null {
  const toastNotification = useNotificationsStore((s) => s.toastNotification)
  const clearToast = useNotificationsStore((s) => s.clearToast)
  const navigate = useNavigate()

  // Auto-dismiss after 5 seconds
  useEffect(() => {
    if (!toastNotification) return
    const timer = setTimeout(() => {
      clearToast()
    }, 5000)
    return () => clearTimeout(timer)
  }, [toastNotification, clearToast])

  if (!toastNotification) return null

  const handleClick = () => {
    console.log('[DEBUG NotificationToast] handleClick, toastNotification:', toastNotification)
    if (toastNotification.conversationId) {
      // A still-staged Live Chat (live_chats.id, pre "Create Conversation")
      // shares the conversationId field with finalized conversations.id —
      // the inbox route only resolves the latter and 404s on the former.
      if (toastNotification.entityType === 'live_chat') {
        navigate(`/agent/chat?conversationId=${toastNotification.conversationId}&channel=chat`)
      } else {
        navigate(`/agent/conversation/inbox?scope=all&conversationId=${toastNotification.conversationId}`)
      }
    }
    clearToast()
  }

  const getIcon = () => {
    if (toastNotification.eventKey === 'ticket_created' || toastNotification.eventKey === 'conversation_assigned') {
      return <MessageSquare className="h-5 w-5 text-white" />
    }
    return <Bell className="h-5 w-5 text-white" />
  }

  const getIconColor = () => {
    if (toastNotification.eventKey === 'ticket_created' || toastNotification.eventKey === 'conversation_assigned') {
      return 'bg-gradient-to-br from-blue-500 to-blue-600'
    }
    return 'bg-gradient-to-br from-gray-500 to-gray-600'
  }

  const clickable = Boolean(toastNotification.conversationId)
  const Wrapper = clickable ? 'button' : 'div'

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 animate-slide-up">
      <div className="flex items-start gap-3 rounded-lg bg-white p-3 shadow-lg ring-1 ring-gray-900/5">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${getIconColor()}`}>
          {getIcon()}
        </div>
        <Wrapper
          type={clickable ? 'button' : undefined}
          onClick={clickable ? handleClick : undefined}
          className={`min-w-0 flex-1 text-left ${clickable ? 'cursor-pointer' : ''}`}
        >
          <p className="truncate text-sm font-semibold text-gray-900">{toastNotification.title}</p>
          {toastNotification.description && (
            <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">{toastNotification.description}</p>
          )}
          {(toastNotification.actorName || toastNotification.assigneeName) && (
            <div className="mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-gray-400">
              {toastNotification.actorName && (
                <span>By {toastNotification.actorName}</span>
              )}
              {toastNotification.actorName && toastNotification.assigneeName && (
                <span aria-hidden>•</span>
              )}
              {toastNotification.assigneeName && (
                <span>Assigned to {toastNotification.assigneeName}</span>
              )}
            </div>
          )}
        </Wrapper>
        <button
          type="button"
          onClick={clearToast}
          className="-mr-1 -mt-1 shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
